import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate} from '../hooks/axiosInstance';

// Fetch all des projects
export const getDesProjects = createAsyncThunk(
  'desproject/getDesProjects',
  async () => {
    const { data } = await axiosPrivate.get(`/admin/desprojects`);
    return data;
  }
);

// Create a new des project (without body)
export const createDesProject = createAsyncThunk(
  'desproject/addDesProject',
  async ({ formData }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        }
      };
      const response = await axiosPrivate.post(`/admin/desproject`, formData, config);
      
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Update des project (excluding body content)
export const updateDesProject = createAsyncThunk(
  'desproject/updateDesProject',
  async ({ id, formData }) => {
    console.log('Updating des project with ID:', id);
    try {
      // Check if formData contains a file
      const hasFile = formData.get('file') !== null;
      
      const config = {
        headers: {
          'Content-Type': hasFile ? 'multipart/form-data' : 'application/json'
        }
      };

      // If no file, convert FormData to regular object for JSON
      let payload = formData;
      if (!hasFile) {
        payload = {};
        for (let [key, value] of formData.entries()) {
          // Exclude body from regular update
          if (key !== 'body') {
            payload[key] = value;
          }
        }
      }

      const response = await axiosPrivate.put(`/admin/desproject/${id}`, payload, config);
      
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Update only body content for des project
export const updateDesProjectBody = createAsyncThunk(
  'desproject/updateDesProjectBody',
  async ({ id, body }) => {
    console.log('Updating des project body with ID:', id);
    try {
      const response = await axiosPrivate.put(`/admin/desproject/${id}/body`, { body }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Get only body content for des project
export const getDesProjectBody = createAsyncThunk(
  'desproject/getDesProjectBody',
  async (id) => {
    console.log('Getting des project body with ID:', id);
    try {
      const response = await axiosPrivate.get(`/admin/desproject/${id}/body`);
      
      return { id, body: response.data.body };
    } catch (error) {
      console.log(error);
      throw error;
    }
  }
);

// Delete des project by ID
export const deleteDesProjectById = createAsyncThunk(
  'desproject/deleteDesProjectById',
  async (projectId, thunkAPI) => {
    console.log('Deleting des project with ID:', projectId);
    try {
      await axiosPrivate.delete(`/admin/desproject/${projectId}`);
      return projectId;
    } catch (error) {
      console.log(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete all des projects
export const deleteAllDesProjects = createAsyncThunk(
  'desproject/deleteAllDesProjects',
  async () => {
    await axiosPrivate.delete(`/admin/desprojects/`);
  }
);

// Fetch all des project images (keeping existing functionality)
export const getDesImgs = createAsyncThunk(
  'desproject/getDesImgs',
  async () => {
    const { data } = await axiosPrivate.get(`/admin/desimgs`);
    return data;
  }
);

// Update des project images (keeping existing functionality)
export const updateDesImgs = createAsyncThunk(
  'desproject/updateDesImgs',
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axiosPrivate.post('/admin/desimg', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const clearField = createAction('desproject/clearField');

export const desprojectSlice = createSlice({
  name: 'desprojects',
  initialState: {
    desprojects: [],
    desproject: null,
    loading: false,
    error: null,
    updating: false,
    bodyLoading: false,
    bodyError: null,
  },
  reducers: {
    clearField: (state) => {
      state.desproject = null;
      state.error = null;
      state.bodyError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get des projects
      .addCase(getDesProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDesProjects.fulfilled, (state, action) => {
        state.desprojects = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getDesProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Create des project
      .addCase(createDesProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDesProject.fulfilled, (state, action) => {
        state.loading = false;
        state.desproject = action.payload;
        // Add new project to the list
        state.desprojects.unshift(action.payload);
        state.error = null;
        console.log('Des project created:', action.payload);
      })
      .addCase(createDesProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update des project
      .addCase(updateDesProject.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateDesProject.fulfilled, (state, action) => {
        state.updating = false;
        const updatedProject = action.payload;
        const index = state.desprojects.findIndex(
          (project) => project._id === updatedProject._id
        );
        if (index !== -1) {
          state.desprojects[index] = updatedProject;
        }
        state.error = null;
        console.log('Des project updated:', updatedProject);
      })
      .addCase(updateDesProject.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message;
      })

      // Update des project body
      .addCase(updateDesProjectBody.pending, (state) => {
        state.bodyLoading = true;
        state.bodyError = null;
      })
      .addCase(updateDesProjectBody.fulfilled, (state, action) => {
        state.bodyLoading = false;
        const updatedProject = action.payload;
        const index = state.desprojects.findIndex(
          (project) => project._id === updatedProject._id
        );
        if (index !== -1) {
          state.desprojects[index] = updatedProject;
        }
        state.bodyError = null;
        console.log('Des project body updated:', updatedProject);
      })
      .addCase(updateDesProjectBody.rejected, (state, action) => {
        state.bodyLoading = false;
        state.bodyError = action.error.message;
      })

      // Get des project body
      .addCase(getDesProjectBody.pending, (state) => {
        state.bodyLoading = true;
        state.bodyError = null;
      })
      .addCase(getDesProjectBody.fulfilled, (state, action) => {
        state.bodyLoading = false;
        const { id, body } = action.payload;
        const index = state.desprojects.findIndex(
          (project) => project._id === id
        );
        if (index !== -1) {
          state.desprojects[index].body = body;
        }
        state.bodyError = null;
        console.log('Des project body fetched:', action.payload);
      })
      .addCase(getDesProjectBody.rejected, (state, action) => {
        state.bodyLoading = false;
        state.bodyError = action.error.message;
      })

      // Delete des project by ID
      .addCase(deleteDesProjectById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDesProjectById.fulfilled, (state, action) => {
        state.loading = false;
        const deletedProjectId = action.payload;
        state.desprojects = state.desprojects.filter(
          (project) => project._id !== deletedProjectId
        );
        state.error = null;
      })
      .addCase(deleteDesProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete all des projects
      .addCase(deleteAllDesProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAllDesProjects.fulfilled, (state) => {
        state.desprojects = [];
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteAllDesProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get des images (existing functionality)
      .addCase(getDesImgs.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDesImgs.fulfilled, (state, action) => {
        state.desprojects = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getDesImgs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update des images (existing functionality)
      .addCase(updateDesImgs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDesImgs.fulfilled, (state, action) => {
        state.loading = false;
        const updatedProject = action.payload;
        const index = state.desprojects.findIndex(
          (project) => project._id === updatedProject._id
        );
        if (index !== -1) {
          state.desprojects[index] = { ...updatedProject };
        } else {
          state.desprojects.push({ ...updatedProject });
        }
      });
  },
});

export default desprojectSlice.reducer;